import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getAuthHeaders } from '../contexts/AuthContext';
import { CheckCircle, Clock, XCircle, Send, ArrowLeft, FileText, Upload, ExternalLink, AlertCircle, Award, ChevronRight, BarChart3, TrendingUp, Eye, Link2, Banknote, CalendarDays } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

function StatusStep({ label, done, active, rejected }) {
  return (
    <div className={`flex items-center gap-2 text-xs ${done ? 'text-[#10B981]' : active ? 'text-[#FF4343]' : rejected ? 'text-red-400' : 'text-[#6B7280]'}`}>
      {done ? <CheckCircle className="w-4 h-4" /> : rejected ? <XCircle className="w-4 h-4" /> : active ? <Clock className="w-4 h-4 animate-pulse" /> : <div className="w-4 h-4 rounded-full border border-white/20" />}
      <span className="font-medium">{label}</span>
    </div>
  );
}

function OffersSection({ onOpenWorkspace }) {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchOffers(); }, []);

  const fetchOffers = async () => {
    try {
      const { data } = await axios.get(`${API}/my/offers`, { headers: getAuthHeaders() });
      setOffers(data);
    } catch {} finally { setLoading(false); }
  };

  const handleAccept = async (id) => {
    try {
      await axios.post(`${API}/my/offers/${id}/accept`, {}, { headers: getAuthHeaders() });
      fetchOffers();
    } catch (e) { alert(e.response?.data?.detail || 'Failed to accept'); }
  };

  const handleDecline = async (id) => {
    if (!window.confirm('Are you sure you want to decline this offer?')) return;
    try {
      await axios.post(`${API}/my/offers/${id}/decline`, {}, { headers: getAuthHeaders() });
      fetchOffers();
    } catch (e) { alert(e.response?.data?.detail || 'Failed to decline'); }
  };

  if (loading) return <p className="text-[#9CA3AF]">Loading offers...</p>;

  const pendingOffers = offers.filter(o => o.status === 'offered');
  const activeDeals = offers.filter(o => ['accepted', 'active', 'submitted'].includes(o.status));

  return (
    <div className="space-y-6" data-testid="offers-section">
      <h2 className="font-display text-xl sm:text-2xl font-bold">Campaign <span className="text-[#FF4343]">Workspace</span></h2>

      {pendingOffers.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-[#FF4343] uppercase tracking-wider">Pending Offers</h3>
          {pendingOffers.map(offer => (
            <div key={offer.id} className="bg-[#111827] border border-[#FF4343]/30 p-4 sm:p-5" data-testid={`offer-${offer.id}`}>
              <div className="flex items-start justify-between gap-3 mb-3">
                <div>
                  <h4 className="font-semibold text-white text-sm sm:text-base">{offer.campaign_title}</h4>
                  <p className="text-xs text-[#9CA3AF] mt-0.5">{offer.brand_name} &middot; {offer.campaign_industry}</p>
                </div>
                {offer.offer_type === 'bespoke' && (
                  <span className="text-[10px] font-mono uppercase tracking-wider px-2 py-1 bg-[#FF4343]/10 text-[#FF4343] border border-[#FF4343]/20 flex-shrink-0">Bespoke</span>
                )}
              </div>
              {offer.offer_message && <p className="text-xs text-[#9CA3AF] mb-3 italic">"{offer.offer_message}"</p>}
              <div className="flex flex-wrap gap-3 mb-4 text-xs text-[#6B7280]">
                {offer.flat_fee && <span>Flat: <span className="text-[#FF4343] font-mono">${Number(offer.flat_fee).toLocaleString()}</span></span>}
                {offer.cpm_rate && <span>CPM: <span className="text-[#FF4343] font-mono">${offer.cpm_rate}</span></span>}
                {offer.custom_bonus && <span>Bonus: <span className="text-[#FF4343] font-mono">${Number(offer.custom_bonus).toLocaleString()}</span></span>}
                {offer.deadline && <span>Deadline: {new Date(offer.deadline).toLocaleDateString()}</span>}
              </div>
              <div className="flex gap-2">
                <button onClick={() => handleAccept(offer.id)} data-testid={`accept-offer-${offer.id}`}
                  className="flex-1 border-2 border-[#FF4343] bg-[#FF4343] text-[#090E33] py-2 text-xs font-semibold transition-all hover:bg-[#E03030]">
                  Accept Offer
                </button>
                <button onClick={() => handleDecline(offer.id)} data-testid={`decline-offer-${offer.id}`}
                  className="px-4 border border-white/20 text-[#9CA3AF] py-2 text-xs hover:text-white hover:bg-white/5 transition-all">
                  Decline
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {activeDeals.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-white uppercase tracking-wider">Active Deals</h3>
          {activeDeals.map(deal => {
            const draftDone = deal.draft_status === 'approved';
            const draftSubmitted = deal.draft_status === 'submitted';
            const draftRevision = deal.draft_status === 'revision_requested';
            const postDone = ['verified', 'approved'].includes(deal.post_status);
            const postSubmitted = deal.status === 'submitted';
            return (
              <button key={deal.id} onClick={() => onOpenWorkspace(deal.id)}
                className="w-full bg-[#111827] border border-white/10 hover:border-white/20 p-4 sm:p-5 text-left transition-all group" data-testid={`deal-${deal.id}`}>
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h4 className="font-semibold text-white text-sm group-hover:text-[#FF4343] transition-colors">{deal.campaign_title}</h4>
                    <p className="text-xs text-[#6B7280] mt-0.5">{deal.brand_name}</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-[#6B7280] group-hover:text-[#FF4343] transition-colors" />
                </div>
                <div className="flex flex-wrap gap-x-4 gap-y-1">
                  <StatusStep label="Accepted" done />
                  <StatusStep label="Draft" done={draftDone} active={draftSubmitted} rejected={draftRevision} />
                  <StatusStep label="Published" done={postDone} active={postSubmitted} />
                  <StatusStep label="Paid" done={deal.payout_status === 'paid'} />
                </div>
              </button>
            );
          })}
        </div>
      )}

      {pendingOffers.length === 0 && activeDeals.length === 0 && (
        <div className="text-center py-12 bg-[#111827] border border-white/10">
          <FileText className="w-10 h-10 text-[#6B7280] mx-auto mb-3" />
          <p className="text-[#9CA3AF] text-sm">No active offers or deals. Browse campaigns to get started.</p>
        </div>
      )}
    </div>
  );
}

function WorkspaceDetail({ participantId, onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [draftText, setDraftText] = useState('');
  const [submittingDraft, setSubmittingDraft] = useState(false);
  const [postUrl, setPostUrl] = useState('');
  const [screenshotUrl, setScreenshotUrl] = useState('');
  const [submittingPost, setSubmittingPost] = useState(false);
  const [payoutStatus, setPayoutStatus] = useState(null);
  const [creatingConnect, setCreatingConnect] = useState(false);
  const [proposedDates, setProposedDates] = useState(['']);
  const [submittingSchedule, setSubmittingSchedule] = useState(false);
  const [netTerms, setNetTerms] = useState('');
  const [savingNetTerms, setSavingNetTerms] = useState(false);

  useEffect(() => { fetchWorkspace(); fetchPayoutStatus(); }, [participantId]);

  const fetchWorkspace = async () => {
    try {
      const { data: ws } = await axios.get(`${API}/workspace/${participantId}`, { headers: getAuthHeaders() });
      setData(ws);
      if (ws.participant?.draft_text) setDraftText(ws.participant.draft_text);
      if (ws.participant?.post_url) setPostUrl(ws.participant.post_url);
      if (ws.participant?.net_terms) setNetTerms(ws.participant.net_terms);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const fetchPayoutStatus = async () => {
    try {
      const { data } = await axios.get(`${API}/connect/account-status`, { headers: getAuthHeaders() });
      setPayoutStatus(data);
    } catch {}
  };

  const setupPayout = async () => {
    setCreatingConnect(true);
    try {
      let accountData = await axios.post(`${API}/connect/create-account`, {}, { headers: getAuthHeaders() });
      const { data: linkData } = await axios.post(`${API}/connect/account-link`, {
        origin_url: window.location.origin,
      }, { headers: getAuthHeaders() });
      if (linkData.url) window.location.href = linkData.url;
    } catch (e) { alert(e.response?.data?.detail || 'Failed to set up payout account'); }
    finally { setCreatingConnect(false); }
  };

  const handleProposeSchedule = async () => {
    const dates = proposedDates.filter(d => d);
    if (dates.length === 0) return;
    setSubmittingSchedule(true);
    try {
      await axios.post(`${API}/workspace/${participantId}/propose-schedule`, { proposed_dates: dates }, { headers: getAuthHeaders() });
      fetchWorkspace();
    } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
    finally { setSubmittingSchedule(false); }
  };

  const handleSetNetTerms = async (terms) => {
    setSavingNetTerms(true);
    try {
      await axios.post(`${API}/workspace/${participantId}/set-net-terms`, { net_terms: terms }, { headers: getAuthHeaders() });
      setNetTerms(terms);
    } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
    finally { setSavingNetTerms(false); }
  };

  const handleSubmitDraft = async () => {
    if (!draftText.trim()) return;
    setSubmittingDraft(true);
    try {
      await axios.post(`${API}/workspace/${participantId}/submit-draft`, { draft_text: draftText }, { headers: getAuthHeaders() });
      fetchWorkspace();
    } catch (e) { alert(e.response?.data?.detail || 'Failed to submit draft'); }
    finally { setSubmittingDraft(false); }
  };

  const handleSubmitPost = async () => {
    if (!postUrl.trim()) return;
    setSubmittingPost(true);
    try {
      await axios.post(`${API}/submissions/${participantId}/submit`, {
        post_url: postUrl,
        platform: data?.participant?.platform || 'LinkedIn',
        screenshot_url: screenshotUrl || undefined,
      }, { headers: getAuthHeaders() });
      fetchWorkspace();
    } catch (e) { alert(e.response?.data?.detail || 'Failed to submit post'); }
    finally { setSubmittingPost(false); }
  };

  if (loading) return <p className="text-[#9CA3AF]">Loading workspace...</p>;
  if (!data) return <p className="text-red-400">Workspace not found.</p>;

  const { participant: part, campaign: camp, brand } = data;
  const draftApproved = part.draft_status === 'approved';
  const draftSubmitted = part.draft_status === 'submitted';
  const draftRevision = part.draft_status === 'revision_requested';
  const postSubmitted = part.status === 'submitted' || part.post_url;
  const postVerified = part.post_status === 'verified' || part.status === 'approved';
  const isPaid = part.payout_status === 'paid';

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="workspace-detail">
      <button onClick={onBack} className="flex items-center gap-2 text-[#9CA3AF] hover:text-white text-sm transition-colors" data-testid="workspace-back">
        <ArrowLeft className="w-4 h-4" /> Back to Workspace
      </button>

      {/* Campaign Header */}
      <div className="bg-[#111827] border border-white/10 p-4 sm:p-6 border-l-[3px] border-l-[#FF4343]">
        <div className="flex items-center gap-3 mb-2">
          {brand?.logo_url && <img src={brand.logo_url} alt="" className="w-8 h-8 object-cover bg-white/10" />}
          <span className="text-xs text-[#9CA3AF]">{brand?.name} {brand?.verified && <span className="text-[#FF4343]">&#10003;</span>}</span>
          {part.offer_type === 'bespoke' && <span className="text-[10px] font-mono uppercase px-2 py-0.5 bg-[#FF4343]/10 text-[#FF4343] border border-[#FF4343]/20">Bespoke Deal</span>}
        </div>
        <h2 className="font-display text-lg sm:text-2xl font-bold mb-1">{camp?.title}</h2>
        <p className="text-xs text-[#6B7280]">{camp?.industry} &middot; Platform: {part.platform} &middot; Deadline: {camp?.deadline ? new Date(camp.deadline).toLocaleDateString() : 'Open'}</p>
      </div>

      {/* Progress Steps */}
      <div className="bg-[#111827] border border-white/10 p-4">
        <div className="flex items-center justify-between gap-2 overflow-x-auto">
          <StatusStep label="Accepted" done />
          <div className="flex-1 h-px bg-white/10" />
          <StatusStep label="Draft" done={draftApproved} active={draftSubmitted} rejected={draftRevision} />
          <div className="flex-1 h-px bg-white/10" />
          <StatusStep label="Published" done={postVerified} active={postSubmitted && !postVerified} />
          <div className="flex-1 h-px bg-white/10" />
          <StatusStep label="Paid" done={isPaid} />
        </div>
      </div>

      {/* Payout Setup Banner */}
      {payoutStatus && !payoutStatus.payouts_enabled && (
        <div className="bg-[#111827] border border-[#FF4343]/30 p-4 sm:p-5" data-testid="payout-setup-banner">
          <div className="flex items-start gap-3">
            <Banknote className="w-5 h-5 text-[#FF4343] flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-white mb-1">Connect Your Bank Account</h4>
              <p className="text-xs text-[#9CA3AF] mb-3">Set up your payout account via Stripe so you can get paid for verified posts. This is required before you can receive payments.</p>
              <button onClick={setupPayout} disabled={creatingConnect} data-testid="setup-payout-btn"
                className="border-2 border-[#FF4343] bg-[#FF4343] text-[#090E33] px-5 py-2 text-xs font-semibold hover:bg-[#E03030] transition-all disabled:opacity-50">
                {creatingConnect ? 'Setting up...' : payoutStatus.connected ? 'Complete Stripe Setup' : 'Connect Bank Account'}
              </button>
            </div>
          </div>
        </div>
      )}
      {payoutStatus?.payouts_enabled && (
        <div className="bg-[#10B981]/10 border border-[#10B981]/20 p-3 flex items-center gap-2" data-testid="payout-connected">
          <CheckCircle className="w-4 h-4 text-[#10B981]" />
          <span className="text-xs text-[#10B981]">Payout account connected — you'll be paid automatically for verified posts</span>
        </div>
      )}

      {/* Campaign Brief */}
      <div className="bg-[#111827] border border-white/10 p-4 sm:p-6">
        <h3 className="font-semibold text-sm text-white mb-3 flex items-center gap-2"><FileText className="w-4 h-4 text-[#FF4343]" /> Campaign Brief</h3>
        <p className="text-sm text-[#9CA3AF] leading-relaxed mb-4">{camp?.description}</p>
        {camp?.talking_points?.length > 0 && (
          <div className="mb-4">
            <h4 className="text-xs font-semibold text-white mb-2 flex items-center gap-1"><CheckCircle className="w-3 h-3 text-[#10B981]" /> Talking Points</h4>
            <ul className="space-y-1">
              {camp.talking_points.map((tp, i) => <li key={i} className="text-xs text-[#9CA3AF] flex items-start gap-2"><span className="text-[#FF4343] mt-0.5">-</span>{tp}</li>)}
            </ul>
          </div>
        )}
        {camp?.guardrails?.length > 0 && (
          <div className="mb-4">
            <h4 className="text-xs font-semibold text-white mb-2 flex items-center gap-1"><AlertCircle className="w-3 h-3 text-red-400" /> Guardrails</h4>
            <ul className="space-y-1">
              {camp.guardrails.map((g, i) => <li key={i} className="text-xs text-[#9CA3AF] flex items-start gap-2"><span className="text-red-400 mt-0.5">-</span>{g}</li>)}
            </ul>
          </div>
        )}
        {camp?.example_post && (
          <div className="border-t border-white/10 pt-3 mt-3">
            <h4 className="text-xs font-semibold text-white mb-2">Example Post</h4>
            <p className="text-xs text-[#9CA3AF] italic">{camp.example_post}</p>
          </div>
        )}
      </div>

      {/* Compensation */}
      <div className="bg-[#111827] border border-white/10 p-4 sm:p-6">
        <h3 className="font-semibold text-sm text-white mb-3 flex items-center gap-2"><Award className="w-4 h-4 text-[#FF4343]" /> Compensation</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {(part.custom_flat_fee || camp?.flat_fee) && (
            <div className="bg-white/5 p-3">
              <p className="text-[10px] text-[#6B7280] uppercase">Flat Fee</p>
              <p className="font-mono text-lg text-[#FF4343] font-bold">${Number(part.custom_flat_fee || camp.flat_fee).toLocaleString()}</p>
            </div>
          )}
          {(part.custom_cpm_rate || camp?.cpm_rate) && (
            <div className="bg-white/5 p-3">
              <p className="text-[10px] text-[#6B7280] uppercase">CPM Rate</p>
              <p className="font-mono text-lg text-[#FF4343] font-bold">${part.custom_cpm_rate || camp.cpm_rate}</p>
            </div>
          )}
          {(part.custom_bonus || camp?.performance_bonus_amount) && (
            <div className="bg-white/5 p-3">
              <p className="text-[10px] text-[#6B7280] uppercase">Bonus</p>
              <p className="font-mono text-lg text-[#FF4343] font-bold">${Number(part.custom_bonus || camp.performance_bonus_amount).toLocaleString()}</p>
            </div>
          )}
        </div>
      </div>

      {/* Post Schedule */}
      {(camp?.posts_per_exec > 1 || part.scheduled_posts?.length > 0) && (
        <div className="bg-[#111827] border border-white/10 p-4 sm:p-6" data-testid="post-schedule">
          <h3 className="font-semibold text-sm text-white mb-3 flex items-center gap-2"><CalendarDays className="w-4 h-4 text-[#FF4343]" /> Post Schedule</h3>
          {part.scheduled_posts?.length > 0 ? (
            <div>
              <div className="flex flex-wrap gap-2 mb-2">
                {part.scheduled_posts.map((sp, i) => (
                  <div key={i} className={`text-xs px-3 py-1.5 border ${sp.status === 'confirmed' ? 'border-[#10B981]/30 text-[#10B981] bg-[#10B981]/5' : sp.status === 'counter_proposed' ? 'border-yellow-500/30 text-yellow-400' : 'border-white/10 text-[#9CA3AF]'}`}>
                    {new Date(sp.date).toLocaleDateString()} — {sp.status === 'confirmed' ? 'Confirmed ✓' : sp.status === 'counter_proposed' ? 'Brand counter-proposed' : 'Proposed'}
                  </div>
                ))}
              </div>
              {part.schedule_status === 'counter_proposed' && (
                <p className="text-xs text-yellow-400 mb-2">The brand suggested different dates. You can accept or propose new ones.</p>
              )}
            </div>
          ) : (
            <div>
              <p className="text-xs text-[#9CA3AF] mb-3">This campaign has {camp?.posts_per_exec || 1} posts. Propose your posting dates for brand approval.</p>
              {proposedDates.map((d, i) => (
                <div key={i} className="flex items-center gap-2 mb-2">
                  <input type="date" value={d} onChange={e => { const nd = [...proposedDates]; nd[i] = e.target.value; setProposedDates(nd); }}
                    className="px-3 py-1.5 bg-white/5 border border-white/10 text-white text-xs focus:outline-none focus:border-[#FF4343]" />
                  {i > 0 && <button onClick={() => setProposedDates(proposedDates.filter((_, j) => j !== i))} className="text-xs text-red-400">Remove</button>}
                </div>
              ))}
              {proposedDates.length < (camp?.posts_per_exec || 1) && (
                <button onClick={() => setProposedDates([...proposedDates, ''])} className="text-xs text-[#FF4343] hover:underline mb-3">+ Add Date</button>
              )}
              <button onClick={handleProposeSchedule} disabled={submittingSchedule || !proposedDates.some(d => d)} data-testid="propose-schedule-btn"
                className="w-full border-2 border-[#FF4343] bg-[#FF4343] text-[#090E33] py-2 text-xs font-semibold hover:bg-[#E03030] transition-all disabled:opacity-50 mt-2">
                {submittingSchedule ? 'Submitting...' : 'Propose Schedule'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Payment Terms (CPM campaigns) */}
      {(camp?.compensation_type === 'cpm' || camp?.compensation_type === 'hybrid') && (
        <div className="bg-[#111827] border border-white/10 p-4 sm:p-6" data-testid="net-terms">
          <h3 className="font-semibold text-sm text-white mb-2 flex items-center gap-2"><Banknote className="w-4 h-4 text-[#FF4343]" /> Payment Timing</h3>
          <p className="text-xs text-[#9CA3AF] mb-4">Choose when you'd like to be paid for performance-based (CPM) posts. A longer window gives your post more time to accumulate impressions — potentially higher earnings — but takes longer to pay out.</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {[
              { key: 'immediate', label: 'Immediate', desc: 'Paid at verification. Impressions locked at that point.', tag: 'Fastest' },
              { key: 'net_7', label: 'Net 7', desc: '7 days after post. More time for impressions to grow.', tag: 'Recommended' },
              { key: 'net_15', label: 'Net 15', desc: '15 days after post. Maximum impression window.', tag: 'Highest potential' },
            ].map(opt => (
              <button key={opt.key} onClick={() => handleSetNetTerms(opt.key)} disabled={savingNetTerms}
                data-testid={`net-terms-${opt.key}`}
                className={`p-3 border text-left transition-all ${netTerms === opt.key ? 'border-[#FF4343] bg-[#FF4343]/10' : 'border-white/10 hover:border-white/20'}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-white">{opt.label}</span>
                  <span className={`text-[10px] px-1.5 py-0.5 ${opt.key === 'net_7' ? 'bg-[#FF4343]/10 text-[#FF4343]' : 'bg-white/5 text-[#6B7280]'}`}>{opt.tag}</span>
                </div>
                <p className="text-[10px] text-[#6B7280]">{opt.desc}</p>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Post Performance */}
      {(part.impressions > 0 || part.post_url) && (
        <div className="bg-[#111827] border border-white/10 p-4 sm:p-6" data-testid="post-performance">
          <h3 className="font-semibold text-sm text-white mb-3 flex items-center gap-2"><BarChart3 className="w-4 h-4 text-[#FF4343]" /> Post Performance</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            <div className="bg-white/5 p-3">
              <p className="text-[10px] text-[#6B7280] uppercase">Impressions</p>
              <p className="font-mono text-lg text-white font-bold">{part.impressions ? Number(part.impressions).toLocaleString() : '—'}</p>
            </div>
            <div className="bg-white/5 p-3">
              <p className="text-[10px] text-[#6B7280] uppercase">Engagement</p>
              <p className="font-mono text-lg text-[#10B981] font-bold">{part.engagement_rate ? `${part.engagement_rate}%` : '—'}</p>
            </div>
            <div className="bg-white/5 p-3">
              <p className="text-[10px] text-[#6B7280] uppercase">CPM</p>
              <p className="font-mono text-lg text-[#FF4343] font-bold">
                {part.impressions > 0 && part.payout_amount > 0
                  ? `$${(part.payout_amount / (part.impressions / 1000)).toFixed(2)}`
                  : '—'}
              </p>
            </div>
            <div className="bg-white/5 p-3">
              <p className="text-[10px] text-[#6B7280] uppercase">Earnings</p>
              <p className="font-mono text-lg text-[#FF4343] font-bold">{part.payout_amount ? `$${Number(part.payout_amount).toLocaleString()}` : '—'}</p>
            </div>
          </div>
          {part.post_url && (
            <a href={part.post_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-xs text-[#FF4343] hover:underline">
              <ExternalLink className="w-3 h-3" /> View Live Post
            </a>
          )}
          {part.verified_at && (
            <p className="text-[10px] text-[#6B7280] mt-2">Verified: {new Date(part.verified_at).toLocaleDateString()}</p>
          )}
        </div>
      )}

      {/* Co-branded Landing Page Link */}
      {camp?.id && (
        <div className="bg-[#111827] border border-white/10 p-4 sm:p-6" data-testid="landing-page-link">
          <h3 className="font-semibold text-sm text-white mb-2 flex items-center gap-2"><Link2 className="w-4 h-4 text-[#FF4343]" /> Campaign Landing Page</h3>
          <p className="text-xs text-[#9CA3AF] mb-3">Share this link in your post or bio to drive traffic to the campaign page.</p>
          <div className="flex items-center gap-2">
            <input readOnly value={`${window.location.origin}/c/${camp.id}`} data-testid="landing-url"
              className="flex-1 px-3 py-2 bg-white/5 border border-white/10 text-white text-xs font-mono" />
            <button onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/c/${camp.id}`); }} data-testid="copy-landing-url"
              className="px-3 py-2 border border-[#FF4343]/50 text-[#FF4343] text-xs hover:bg-[#FF4343]/10 transition-all">
              Copy
            </button>
          </div>
        </div>
      )}

      {/* Draft Section */}
      {!postVerified && (
        <div className="bg-[#111827] border border-white/10 p-4 sm:p-6">
          <h3 className="font-semibold text-sm text-white mb-3 flex items-center gap-2"><Send className="w-4 h-4 text-[#FF4343]" /> Draft Post</h3>
          {draftRevision && part.draft_feedback && (
            <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 text-xs text-red-400">
              <strong>Revision requested:</strong> {part.draft_feedback}
            </div>
          )}
          {draftSubmitted && (
            <div className="mb-4 p-3 bg-yellow-500/10 border border-yellow-500/20 text-xs text-yellow-400">
              Draft submitted — waiting for brand approval.
            </div>
          )}
          {draftApproved && (
            <div className="mb-4 p-3 bg-[#10B981]/10 border border-[#10B981]/20 text-xs text-[#10B981]">
              Draft approved! Publish your post and submit the live link below.
            </div>
          )}
          <textarea
            value={draftText}
            onChange={e => setDraftText(e.target.value)}
            placeholder="Write your draft post here..."
            rows={5}
            disabled={draftSubmitted && !draftRevision}
            data-testid="draft-textarea"
            className="w-full px-3 py-2 bg-white/5 border border-white/10 text-white placeholder-[#6B7280] text-sm focus:outline-none focus:border-[#FF4343] resize-none disabled:opacity-50"
          />
          {(!draftSubmitted || draftRevision) && (
            <button onClick={handleSubmitDraft} disabled={submittingDraft || !draftText.trim()} data-testid="submit-draft-btn"
              className="mt-3 w-full border-2 border-[#FF4343] bg-[#FF4343] text-[#090E33] py-2 text-xs font-semibold transition-all hover:bg-[#E03030] disabled:opacity-50">
              {submittingDraft ? 'Submitting...' : draftRevision ? 'Resubmit Draft' : 'Submit Draft for Approval'}
            </button>
          )}
        </div>
      )}

      {/* Post Submission Section */}
      {draftApproved && !postVerified && (
        <div className="bg-[#111827] border border-white/10 p-4 sm:p-6">
          <h3 className="font-semibold text-sm text-white mb-3 flex items-center gap-2"><Upload className="w-4 h-4 text-[#FF4343]" /> Submit Live Post</h3>
          <p className="text-xs text-[#9CA3AF] mb-4">After publishing your post, paste the link and upload a screenshot for verification.</p>
          <div className="space-y-3">
            <div>
              <label className="text-[10px] text-[#6B7280] uppercase mb-1 block">Post URL</label>
              <input value={postUrl} onChange={e => setPostUrl(e.target.value)} placeholder="https://linkedin.com/posts/..." data-testid="post-url-input"
                className="w-full px-3 py-2 bg-white/5 border border-white/10 text-white placeholder-[#6B7280] text-sm focus:outline-none focus:border-[#FF4343]" />
            </div>
            <div>
              <label className="text-[10px] text-[#6B7280] uppercase mb-1 block">Screenshot URL (optional)</label>
              <input value={screenshotUrl} onChange={e => setScreenshotUrl(e.target.value)} placeholder="Upload screenshot URL" data-testid="screenshot-url-input"
                className="w-full px-3 py-2 bg-white/5 border border-white/10 text-white placeholder-[#6B7280] text-sm focus:outline-none focus:border-[#FF4343]" />
            </div>
            <button onClick={handleSubmitPost} disabled={submittingPost || !postUrl.trim()} data-testid="submit-post-btn"
              className="w-full border-2 border-[#FF4343] bg-[#FF4343] text-[#090E33] py-2 text-xs font-semibold transition-all hover:bg-[#E03030] disabled:opacity-50">
              {submittingPost ? 'Submitting...' : 'Submit Live Post'}
            </button>
          </div>
        </div>
      )}

      {/* Payment Status */}
      {postVerified && (
        <div className={`bg-[#111827] border p-4 sm:p-6 ${isPaid ? 'border-[#10B981]/30' : 'border-white/10'}`}>
          <h3 className="font-semibold text-sm text-white mb-2">Payment Status</h3>
          {isPaid ? (
            <div className="flex items-center gap-2 text-[#10B981]">
              <CheckCircle className="w-5 h-5" />
              <span className="font-mono text-lg font-bold">${Number(part.payout_amount || 0).toLocaleString()}</span>
              <span className="text-xs">Paid</span>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-yellow-400">
              <Clock className="w-5 h-5" />
              <span className="text-sm">Post verified — payment processing</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function CampaignWorkspace() {
  const [activeWorkspace, setActiveWorkspace] = useState(null);

  if (activeWorkspace) {
    return <WorkspaceDetail participantId={activeWorkspace} onBack={() => setActiveWorkspace(null)} />;
  }

  return <OffersSection onOpenWorkspace={(id) => setActiveWorkspace(id)} />;
}
